import axios from 'axios';
import { Lov } from '../models/lov';
import { Machine } from '../models/machine';
import { Report } from '../models/report';
import { ENV } from '../environment';
import pool from 'generic-promise-pool'
import { O_EXCL } from 'constants';

export class Datasource {

  // Singleton
  static shared:Datasource = (() => {
    return new Datasource()
  })();

  // Variables
  client:any;

  // Constructor
  private constructor() { 
    // TODO: Configure this
    const conf = {
      endpoint: ENV.define('//mrg.maketh.dev/', 'http://localhost:8090')
    };
    this.client = axios.create({
      baseURL: conf.endpoint,
      timeout: 3000
    })
    console.log('Configured with', conf.endpoint)
  }

  // LOV

  lov(): Promise<Lov[]> {
    return this.client.get('lov/')
      .then((resp:any) => {
        const raw:any[] = resp.data.data
        return raw.length && raw.map((o) => new Lov(o)) || []
      })
  }

  updateLov(updatedValue:Lov) {
    return this.client.put(`lov/${updatedValue.id}`, updatedValue)
  }

  createLov(newLov:Lov): Promise<number> {
    return this.client.post('lov', newLov)
      .then((resp:any) => {
        console.info('RAW', resp)
        const insertId:number = +resp.data.success.insert_id
        return insertId
      })
  }

  deleteLov(lovId:number): Promise<any> {
    return this.client.delete(`lov/${lovId}/`)
  }

  // MACHINES

  machines(offset:number = 0, limit:number = 30): Promise<[Machine[], number]> {
    return this.client.get(`machines/?limit=${limit}&offset=${offset}`)
      .then((resp:any) => {
        const raw:any[] = resp.data.data || []
        const data = raw.length && raw.map((o) => new Machine(o)) || []
        return [data, resp.data.total]
      })
  }

  searchMachines(term:string): Promise<[Machine[], number]> {
    return this.client.get(`machines/?search[serial_number]=${term}`)
      .then((resp:any) => {
        const raw:any[] = resp.data.data || []
        const data = raw.length && raw.map((o) => new Machine(o)) || []
        return [data, resp.data.total]
      })
  }

  uploadMachines(fileRef:any): Promise<number> {
    let formData = new FormData()
    formData.append('FILE', fileRef)
    return this.client.post('/machines/upload', 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      ).then(((resp:any) => {
        console.log(resp)
        return resp.data.success.records_processed || -1
      }))
  }

  /**
   * Orchastrate Parallel Promise Handling with genric-promise-pool
   *
   * @param blobs
   */
  async uploadPhotos(target: string, blobs: Blob[]): Promise<string[]> {
    let uploadPool = pool.create({
      name: 'Upload Pool',
      max: 4,
      min: 1,
      create: () => {
        return { /* just token */ }
      },
      destroy: () => { /* no need to do anything */ }
    })
    // array of promises
    let arr: Promise<string>[] = []
    for(let blob of blobs) {
      arr.push(uploadPool.acquire(() => {
        return this.uploadPhoto(target, blob)
      }))
    }
    return Promise.all(arr).finally(() => {
      uploadPool.drain()
    })
  }

  async uploadPhoto(target: string, blob: Blob): Promise<string> {
    let formData = new FormData()
    formData.append('FILE', blob)
    const response = await this.client.post('/photo/upload',
        formData,
        {
          params: { target },
          headers: { 'Content-Type': 'multipart/form-data' }
        })
    // upload result
    if (response.data.error) {
      throw new Error('Invalid request: ' + response.data.error)
    }
    console.log('Uploaded', response.data.success.url)
    
    // Return URL
    return response.data.success.url
  }

  // REPORTS

  reports(offset:number = 0, limit:number = 30): Promise<[Report[], number]> {
    return this.client.get(`reports/?limit=${limit}&offset=${offset}`)
      .then((resp:any) => {
        const raw:any[] = resp.data.data
        const data = raw.length && raw.map((o) => new Report(o)) || []
        return [data, resp.data.total]
      })
  }

  report(id:string): Promise<Report> {
    return this.client.get(`reports/${id}/`)
      .then((resp:any) => {
        const raw:any = resp.data
        return new Report(raw)
      })
  }

  updateReport(updatedReport:Report): Promise<number> {
    const id = updatedReport.id
    return this.client.put(`reports/${id}`, updatedReport.toJson())
      .then((resp:any) => {
        console.log('EDIT -> RAW', resp)
        return updatedReport.id
      })
  }

  createReport(newReport:Report): Promise<number> {
    const json = newReport.toJson()
    delete json.id;
    return this.client.post('reports', json)
      .then((resp:any) => {
        console.log('CREATE -> RAW', resp)
        const insertId:number = +resp.data.success.insert_id
        return insertId
      })
  }

  deleteReport(id:number): Promise<number> {
    return this.client.delete(`reports/${id}`)
      .then((resp:any) => {
        console.log(`DELETE REPORT(${id})`, resp)
        return id
      })
  }

  // TOOLS

  migrate(): Promise<any> {
    return this.client.post('db/migrate')
      .then((resp: any) => {
        return resp.data
      })
  }
}
